'use client';
import { track as vercelTrack } from '@vercel/analytics';
import type React from 'react';
import { createContext, PropsWithChildren, useCallback, useContext, useMemo } from 'react';

type TrackFunction = (event: string, data?: Record<string, unknown>) => void;

type AnalyticsContext = {
    track: TrackFunction;
    /**
     * Trackers contributed by this provider and its ancestors. A nested provider replaces the
     * context entirely, so without passing them down an app-level tracker (e.g. PostHog) would
     * silently stop receiving events inside any subtree that mounts its own provider.
     */
    trackers: TrackFunction[];
};
const AnalyticsContext = createContext<AnalyticsContext | null>(null);

const AnalyticsProvider: React.FC<
    PropsWithChildren<{
        overallData?: Record<string, string>;
        eventPrefix?: string;
        trackers?: TrackFunction[];
    }>
> = ({ children, overallData = {}, eventPrefix, trackers = [] }) => {
    const parentTrackers = useContext(AnalyticsContext)?.trackers;

    // Inherited first, own last — order only matters for tracker side effects, not for delivery.
    const inheritedTrackers = useMemo(() => [...(parentTrackers ?? []), ...trackers], [parentTrackers, trackers]);

    const allTrackers = useMemo<TrackFunction[]>(
        () => [(event, data) => vercelTrack(event, data as Record<string, string>), ...inheritedTrackers],
        [inheritedTrackers]
    );

    const track = useCallback(
        (event: string, data: Record<string, unknown> = {}) => {
            const prefixedEvent = eventPrefix ? `${eventPrefix}: ${event}` : event;
            const mergedData = { ...overallData, ...data };

            for (const t of allTrackers) {
                t(prefixedEvent, mergedData);
            }
        },
        [allTrackers, overallData, eventPrefix]
    );

    const context = useMemo(() => ({ track, trackers: inheritedTrackers }), [track, inheritedTrackers]);
    return <AnalyticsContext.Provider value={context}>{children}</AnalyticsContext.Provider>;
};

export const useAnalytics = () => {
    const context = useContext(AnalyticsContext);
    if (!context) {
        // fallback to vercel track, no crashing if no provider is configured
        return {
            track: vercelTrack
        };
    }
    return context;
};

export default AnalyticsProvider;
